import React, { useEffect } from "react";
import { Badge } from "reactstrap";
import { TicketAttachment, TicketMessage } from "./types";
import {
  formatDateTime,
  formatFileSize,
  getAttachmentIconClass,
  getUserInitials,
} from "./utils";

type MessageBubbleProps = {
  message: TicketMessage;
  previewUrls: Record<number, string>;
  previewFailed: Record<number, boolean>;
  onDownloadAttachment: (attachment: TicketAttachment) => void;
  onOpenImagePreview: (attachment: TicketAttachment) => void;
  onEnsurePreview: (attachment: TicketAttachment) => void;
  currentUserId: number;
};

type ImageAttachmentPreviewProps = {
  attachment: TicketAttachment;
  previewUrl?: string;
  previewFailed?: boolean;
  onOpenImagePreview: (attachment: TicketAttachment) => void;
  onEnsurePreview: (attachment: TicketAttachment) => void;
};

const ImageAttachmentPreview = React.memo(({
  attachment,
  previewUrl,
  previewFailed,
  onOpenImagePreview,
  onEnsurePreview,
}: ImageAttachmentPreviewProps) => {
  useEffect(() => {
    if (previewUrl || previewFailed) {
      return;
    }
    onEnsurePreview(attachment);
  }, [attachment, onEnsurePreview, previewFailed, previewUrl]);

  return (
    <div className="message-img mt-2">
      {previewUrl ? (
        <div className="message-img-list">
          <img
            src={previewUrl}
            alt={attachment.original_name}
            style={{
              maxWidth: "220px",
              maxHeight: "180px",
              objectFit: "cover",
              cursor: "zoom-in",
            }}
            className="rounded border"
            onClick={() => onOpenImagePreview(attachment)}
          />
        </div>
      ) : previewFailed ? (
        <small className="text-muted">Image preview unavailable</small>
      ) : (
        <small className="text-muted">Loading preview...</small>
      )}
    </div>
  );
});

ImageAttachmentPreview.displayName = "ImageAttachmentPreview";

const MessageBubble = React.memo(({
  message,
  previewUrls,
  previewFailed,
  onDownloadAttachment,
  onOpenImagePreview,
  onEnsurePreview,
  currentUserId,
}: MessageBubbleProps) => {
  const isOwnMessage = message.author?.id === currentUserId;
  const attachmentCardClass = isOwnMessage
    ? "bg-white border rounded p-2 text-start"
    : "border border-white border-opacity-25 rounded p-2 text-start";
  const attachmentButtonClass = isOwnMessage
    ? "btn btn-link btn-sm p-0 text-start text-primary"
    : "btn btn-link btn-sm p-0 text-start text-white";
  const attachmentMetaClass = isOwnMessage ? "small text-muted" : "small text-white-50";
  const attachmentIconClass = isOwnMessage ? "text-primary" : "text-white";

  return (
    <li className={isOwnMessage ? "right" : ""}>
      <div className="conversation-list">
        <div className="ctext-wrap">
          <div className="ctext-wrap-content">
            <div className="conversation-name">
              <span className="user-name">{isOwnMessage ? "You" : message.author?.name || "System"}</span>
              <span className="time">{formatDateTime(message.created_at)}</span>
            </div>

            {message.is_internal && (
              <div className="mb-2">
                <Badge color="warning" className="font-size-10">
                  Internal note
                </Badge>
              </div>
            )}

            {message.body ? (
              <p className="mb-2 text-break" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {message.body}
              </p>
            ) : null}

            {!!message.attachments?.length && (
              <div className="d-grid gap-2">
                {message.attachments.map((attachment) => {
                  const isBroken = !!attachment.is_broken;
                  return (
                    <div key={attachment.id} className={attachmentCardClass}>
                      <div className="d-flex align-items-start gap-2">
                        <i className={`${getAttachmentIconClass(attachment)} font-size-18 mt-1 ${attachmentIconClass}`} />
                        <div className="flex-grow-1">
                          {isBroken ? (
                            <span className={`${attachmentButtonClass} text-decoration-line-through`}
                                  style={{ cursor: "not-allowed", opacity: 0.7 }}>
                              {attachment.original_name}
                            </span>
                          ) : (
                            <button
                              type="button"
                              className={attachmentButtonClass}
                              onClick={() => onDownloadAttachment(attachment)}
                            >
                              {attachment.original_name}
                            </button>
                          )}
                          <div className={attachmentMetaClass}>
                            {formatFileSize(attachment.size_bytes)}
                            {isBroken
                              ? " • Файл битий"
                              : attachment.is_image ? " • Image preview available" : ""}
                          </div>
                        </div>
                        <button
                          type="button"
                          className={`btn btn-sm ${isOwnMessage ? "btn-light" : "btn-primary"} py-0 px-2`}
                          onClick={() => !isBroken && onDownloadAttachment(attachment)}
                          disabled={isBroken}
                          title={isBroken ? "Файл битий" : "Download"}
                        >
                          <i className="bx bx-download" />
                        </button>
                      </div>
                      {attachment.is_image && !isBroken && (
                        <ImageAttachmentPreview
                          attachment={attachment}
                          previewUrl={previewUrls[attachment.id]}
                          previewFailed={previewFailed[attachment.id]}
                          onOpenImagePreview={onOpenImagePreview}
                          onEnsurePreview={onEnsurePreview}
                        />
                      )}
                      {attachment.is_image && isBroken && (
                        <div className="message-img mt-2">
                          <small className="text-muted">Файл битий — превʼю недоступне</small>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </li>
  );
});

MessageBubble.displayName = "MessageBubble";

export default MessageBubble;
